import type { SupabaseClient } from '@supabase/supabase-js';
import {
  createGrid,
  type ColDef,
  type GridApi,
  type GridOptions,
  type ICellRendererParams,
  type IDatasource,
  type IFloatingFilterComp,
  type IFloatingFilterParams,
  type IGetRowsParams,
} from 'ag-grid-community';
import { renderKfPage, kfJsonPayload, KF_ICON, type KfPage } from '@vario-connect/frontend-design';
import { varioDocumentUrl, varioArticleUrl } from '../../lib/vario-links.js';
import { helpButtonHtml, wireHelpButtons } from '../../lib/help-panel.js';
import { awaitOrderOutcome, latestActivityId } from '../../lib/await-order-outcome.js';
import { infoBadge } from '../../lib/info-badge.js';

interface LogCtx {
  supabase: SupabaseClient;
  session: { token: string; tenantId: string };
  supabaseUrl: string;
}

interface LogRow {
  id: number;
  event: string;
  status: string; // 'success' | 'error'
  storefront: string | null;
  ref_id: string | null;
  script_name: string | null;
  summary: string | null;
  detail: Record<string, unknown> | null;
  created_at: string;
}

const GRID_BLOCK_SIZE = 50;

interface FfOption { value: string; label: string }

/** Feste Dropdown-Optionen der Spalten-Filterzeile. Leerer Wert = „alle" (kein Filter).
 *  Vorgang-Werte = DB-`event`, Status-Werte = DB-`status`. */
/** Vorgänge, deren `ref_id` eine Gambio-Bestellnummer ist (nicht eine Artikel-/Vario-ID). */
const ORDER_REF_EVENTS = new Set(['order_import', 'order_fix']);

const EVENT_OPTIONS: FfOption[] = [
  { value: '', label: 'alle' },
  { value: 'order_import', label: 'Auftragsimport' },
  { value: 'article_import', label: 'Artikelimport' },
  { value: 'listing_publish', label: 'Aufschalten' },
];
const STATUS_OPTIONS: FfOption[] = [
  { value: '', label: 'alle' },
  { value: 'success', label: 'Erfolg' },
  { value: 'warning', label: 'Warnung' },
  { value: 'error', label: 'Fehler' },
];

/** Zusatz-Params, die je Spalte via `floatingFilterComponentParams` reingereicht werden. */
interface DropdownFfParams extends IFloatingFilterParams {
  ffOptions: FfOption[];
  /** aktueller Filterwert (aus dem Closure-State), damit die Auswahl beim Neuaufbau erhalten bleibt. */
  getValue: () => string;
  onChange: (value: string) => void;
  /** erlaubt dem Aufrufer, die Optionen nachträglich zu setzen (Skript-Liste lädt asynchron). */
  register?: (api: { setOptions: (opts: FfOption[]) => void }) => void;
}

/** Wiederverwendbarer Dropdown-Floating-Filter: rendert ein <select> in der Filterzeile
 *  unter dem Spaltenkopf. Treibt die Filterung SELBST über den Closure-State + reload()
 *  (Server-seitig, Infinite-Row-Model) - bewusst OHNE ag-Grid-Filtermodell. */
class DropdownFloatingFilter implements IFloatingFilterComp {
  private eGui!: HTMLElement;
  private select!: HTMLSelectElement;
  private getValue: () => string = () => '';

  init(params: DropdownFfParams): void {
    this.getValue = params.getValue;
    this.eGui = document.createElement('div');
    this.eGui.className = 'go-ff';
    this.select = document.createElement('select');
    this.select.className = 'go-ff-sel';
    this.setOptions(params.ffOptions);
    this.select.addEventListener('change', () => params.onChange(this.select.value));
    this.eGui.appendChild(this.select);
    params.register?.({ setOptions: (opts) => this.setOptions(opts) });
  }

  private setOptions(opts: FfOption[]): void {
    const current = this.getValue();
    this.select.replaceChildren(
      ...opts.map((o) => {
        const el = document.createElement('option');
        el.value = o.value;
        el.textContent = o.label;
        return el;
      }),
    );
    // Auswahl beibehalten, falls noch vorhanden - sonst auf „alle" (leerer Wert).
    this.select.value = opts.some((o) => o.value === current) ? current : '';
  }

  onParentModelChanged(): void {
    // no-op: wir nutzen das ag-Grid-Filtermodell nicht (siehe Klassen-Doc).
  }

  getGui(): HTMLElement {
    return this.eGui;
  }
}

/** Aktivitaets-Log der Gambio-App im neuen go-eCommerce-Fenster-Design (kf-page).
 *  Tabelle "was ist passiert" - pro Vorgang eine Zeile; Detail-Drawer zeigt das
 *  eingereichte JSON + Ergebnis/Fehler, Fehlversuche bieten "Erneut verarbeiten". */
export function renderLogTab(host: HTMLElement, ctx: LogCtx, params?: unknown): void {
  const p = (params !== null && typeof params === 'object') ? (params as { embedded?: boolean; focusRef?: unknown }) : {};
  const opts: { embedded?: boolean } = p.embedded !== undefined ? { embedded: p.embedded } : {};
  const focusRef = typeof p.focusRef === 'string' ? p.focusRef : '';
  // Kopf mit „● Gambio verbunden"-Kennung (wie die übrigen Verkaufskanal-Tabs / Otto-Modul).
  // Die Aktionen (Export/Aktualisieren) liegen bewusst in der Toolbar (siehe „toolbar wiring"
  // unten), nicht im Header - so bleibt der Kopf schlicht wie im Produkte-Tab.
  const page = renderKfPage(host, {
    eyebrow: 'Gambio · Protokoll',
    title: 'Log-Übersicht',
    status: 'Gambio verbunden',
    logo: '<img src="/gambio-logo.png?v=2" alt="Gambio" />',
    logoTitle: 'Gambio',
    search: { placeholder: 'Referenz, Beleg oder Meldung suchen…' },
    showColConfig: true,
    fitViewport: !opts.embedded,
  });
  // renderKfPage escaped den Titel (kein HTML im opts.title möglich) - Badge daher per DOM
  // an die schon gerenderte Überschrift angehängt statt über die Titel-Option.
  page.root.querySelector('.htitle h1')?.insertAdjacentHTML(
    'beforeend',
    ` ${infoBadge('Protokoll aller Vorgänge mit Status und Details. Fehler erscheinen hier im Klartext.')}`,
  );
  page.root.querySelector('.apphead .hactions')?.insertAdjacentHTML('afterbegin', helpButtonHtml('protokoll'));
  wireHelpButtons(page.root);

  // Filterzeile-State (leer = „alle"). focusRef (Deep-Link aus einem Order-Import-Fehler)
  // springt direkt auf Status = Fehler; der Status-Dropdown liest diesen Wert beim Init.
  let statusFilter = focusRef ? 'error' : '';
  let eventFilter = '';
  let scriptFilter = '';
  let searchText = focusRef;
  let lastTotal: number | undefined;

  // Skript-Optionen laden asynchron (distinct script_name) - bis dahin nur „alle".
  // scriptFfApi wird vom Floating-Filter beim Init registriert (setzt die Optionen nach).
  let scriptFfApi: { setOptions: (opts: FfOption[]) => void } | null = null;
  let scriptOptions: FfOption[] = [{ value: '', label: 'alle' }];

  const datasource: IDatasource = {
    getRows: async (params: IGetRowsParams) => {
      try {
        const { rows, total } = await loadLogPage(ctx, params.startRow, params.endRow, {
          status: statusFilter, event: eventFilter, script: scriptFilter, search: searchText,
        });
        lastTotal = total;
        params.successCallback(rows, total);
      } catch (e) {
        console.error('[log] datasource.getRows failed', e);
        params.failCallback();
      }
    },
  };

  const cols: ColDef<LogRow>[] = [
    {
      headerName: 'Zeitpunkt', field: 'created_at', width: 160,
      cellRenderer: (p: ICellRendererParams<LogRow>) => {
        const { date, time } = splitDateTime(p.data?.created_at ?? null);
        return htmlEl(`<span class="c-time">${escapeHtml(date)}, <span class="t2">${escapeHtml(time)}</span></span>`);
      },
    },
    {
      headerName: 'Vorgang', field: 'event', width: 140, valueFormatter: (p) => eventLabel(p.value),
      filter: true, floatingFilter: true, floatingFilterComponent: DropdownFloatingFilter,
      floatingFilterComponentParams: {
        ffOptions: EVENT_OPTIONS,
        getValue: () => eventFilter,
        onChange: (v: string) => { eventFilter = v; reload(); },
      },
    },
    {
      headerName: 'SF', field: 'storefront', width: 70,
      cellRenderer: (p: ICellRendererParams<LogRow>) => htmlEl(`<span class="c-sf">${escapeHtml(p.data?.storefront ?? '')}</span>`),
    },
    {
      headerName: 'Referenz', field: 'ref_id', width: 130,
      cellRenderer: (p: ICellRendererParams<LogRow>) => {
        const ref = p.data?.ref_id;
        if (!ref) return htmlEl('');
        // order_import/order_fix: Referenz = Gambio-Bestellnummer → in die Bestellübersicht der App
        // springen. Kein target="_blank" wie beim Vario-Artikel: das Ziel ist ein eigener Slot DIESER
        // App, der im iframe geöffnet wird (openOrdersSlot behält den appToken, keine Re-Anmeldung).
        if (ORDER_REF_EVENTS.has(p.data?.event ?? '') && p.data?.storefront) {
          const shop = p.data.storefront;
          const el = htmlEl(`<a href="#" class="c-ref" title="Bestellung in der Bestellübersicht öffnen" style="text-decoration:underline;text-underline-offset:2px;">${escapeHtml(ref)}</a>`);
          el.querySelector('a')?.addEventListener('click', (ev) => {
            ev.preventDefault();
            openOrdersSlot(ref, shop);
          });
          return el;
        }
        // article_import: Referenz = Artikel-/Produktnummer → Deeplink zum Vario-Artikel (Hauptartikel).
        const url = varioArticleUrl(logArticleId(p.data));
        return htmlEl(url
          ? `<a href="${escapeHtml(url)}" target="_blank" rel="noopener" class="c-ref" title="Artikel in Vario öffnen" style="text-decoration:underline;text-underline-offset:2px;">${escapeHtml(ref)}</a>`
          : `<span class="c-ref">${escapeHtml(ref)}</span>`);
      },
    },
    {
      headerName: 'Skript', field: 'script_name', width: 196,
      cellRenderer: (p: ICellRendererParams<LogRow>) => htmlEl(`<span class="c-script">${escapeHtml(p.data?.script_name ?? '')}</span>`),
      filter: true, floatingFilter: true, floatingFilterComponent: DropdownFloatingFilter,
      floatingFilterComponentParams: {
        ffOptions: scriptOptions,
        getValue: () => scriptFilter,
        onChange: (v: string) => { scriptFilter = v; reload(); },
        register: (api: { setOptions: (opts: FfOption[]) => void }) => { scriptFfApi = api; },
      },
    },
    {
      headerName: 'Status', field: 'status', width: 130,
      cellRenderer: (p: ICellRendererParams<LogRow>) => htmlEl(statusPill(p.data?.status ?? '')),
      filter: true, floatingFilter: true, floatingFilterComponent: DropdownFloatingFilter,
      floatingFilterComponentParams: {
        ffOptions: STATUS_OPTIONS,
        getValue: () => statusFilter,
        onChange: (v: string) => { statusFilter = v; reload(); },
      },
    },
    {
      headerName: 'Meldung', field: 'summary', flex: 1, minWidth: 220,
      cellRenderer: (p: ICellRendererParams<LogRow>) => htmlEl(messageHtml(p.data)),
    },
    {
      headerName: 'Aktionen', colId: 'actions', width: 160, sortable: false, resizable: false, pinned: 'right',
      cellRenderer: () => actionsCell(),
    },
  ];

  const options: GridOptions<LogRow> = {
    columnDefs: cols,
    defaultColDef: { sortable: false, filter: false, resizable: true, suppressHeaderMenuButton: true },
    rowModelType: 'infinite',
    cacheBlockSize: GRID_BLOCK_SIZE,
    maxBlocksInCache: 10,
    infiniteInitialRowCount: 50,
    blockLoadDebounceMillis: 100,
    datasource,
    rowHeight: 46,
    headerHeight: 48,
    floatingFiltersHeight: 46,
    pagination: true,
    paginationPageSize: 50,
    paginationPageSizeSelector: [25, 50, 100],
    animateRows: true,
    suppressCellFocus: true,
    getRowId: (p) => (p.data ? String(p.data.id) : ''),
    onCellClicked: (e) => {
      if (e.colDef.headerName !== 'Aktionen' || !e.data) return;
      const target = e.event?.target as HTMLElement | null;
      const act = target?.closest<HTMLElement>('[data-act]')?.dataset.act;
      if (act === 'reprocess') void retryImport(e.data, ctx, page, refreshAll);
      else openDrawer(e.data, page, ctx, refreshAll);
    },
    onModelUpdated: () => updateMeta(),
  };

  // Reihenfolge bewusst: gridApi VOR createGrid deklarieren. createGrid kann
  // onModelUpdated synchron feuern (→ updateMeta), bevor die Zuweisung zurueck
  // ist - daher Guard auf gridApi (sonst Laufzeit-TDZ trotz gruenem tsc/vite,
  // siehe CLAUDE.md). lastTotal (gefiltert-exakt) kommt aus loadLogPage.
  let gridApi: GridApi<LogRow> | undefined;

  function updateMeta(): void {
    if (!gridApi) return;
    const shown = gridApi.getDisplayedRowCount();
    const total = lastTotal ?? shown;
    page.setMeta(`<b>${shown}</b> von ${total} Einträgen`);
  }

  const reload = () => gridApi?.setGridOption('datasource', datasource);

  // Nach Beheben/Erneut-verarbeiten: Grid + Meta-Zeile ("X von Y Einträgen") neu laden.
  // reload() stößt die Infinite-Row-Model-Datasource neu an; deren getRows aktualisiert
  // lastTotal, onModelUpdated ruft daraufhin updateMeta() automatisch.
  const refreshAll = (): void => {
    reload();
  };

  gridApi = createGrid(page.gridEl, options);

  // focusRef: beim Öffnen direkt den passenden Fehler-Drawer zeigen.
  if (focusRef) {
    void (async () => {
      const { data } = await ctx.supabase
        .schema('gambio').from('activity_log')
        .select('id,event,status,storefront,ref_id,script_name,summary,detail,created_at')
        .eq('event', 'order_import').eq('status', 'error').eq('ref_id', focusRef)
        .order('created_at', { ascending: false }).limit(1);
      const row = (data ?? [])[0] as LogRow | undefined;
      if (row) openDrawer(row, page, ctx, refreshAll);
    })();
  }

  // Distinct Skript-Namen für den Skript-Dropdown nachladen (asynchron) und in den
  // registrierten Floating-Filter einspielen.
  const applyScriptOptions = (names: string[]) => {
    scriptOptions = [{ value: '', label: 'alle' }, ...names.map((n) => ({ value: n, label: n }))];
    scriptFfApi?.setOptions(scriptOptions);
  };
  void loadScriptNames(ctx).then(applyScriptOptions);

  // ---- toolbar wiring ----
  let searchTimer: ReturnType<typeof setTimeout> | undefined;
  page.onSearch((text) => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      searchText = text.trim();
      reload();
    }, 250);
  });
  // Aktionen liegen jetzt in der Toolbar (nicht im Header - Kopf schlicht wie beim Produkte-Tab,
  // „Gambio-Status:"-Label frei). Nach dem Render in die rechte Toolbar-Gruppe (.tb-right)
  // einhängen und direkt verdrahten (renderKfPage verkabelt nur beim Render vorhandene Buttons).
  const doRefresh = (): void => {
    // KEIN manuelles showLoadingOverlay: beim Infinite-Row-Model wird ein manuell gezeigtes
    // Overlay nicht automatisch wieder versteckt (kein hideOverlay) und bliebe dauerhaft hängen.
    // reload() (setGridOption datasource) holt frisch und zeigt die ag-Grid-eigenen Lade-Zeilen.
    reload();
    void loadScriptNames(ctx).then(applyScriptOptions);
    page.toast('Protokoll aktualisiert');
  };
  const doExport = (): void => {
    gridApi?.exportDataAsCsv({
      fileName: 'gambio-log.csv',
      columnKeys: ['created_at', 'event', 'storefront', 'ref_id', 'script_name', 'status', 'summary'],
    });
    page.toast('CSV exportiert');
  };
  const tbRight = page.root.querySelector('.tb-right');
  if (tbRight) {
    const exportBtn = document.createElement('button');
    exportBtn.type = 'button';
    exportBtn.className = 'btn btn-ghost';
    exportBtn.innerHTML = `${KF_ICON.export}Exportieren`;
    exportBtn.addEventListener('click', doExport);
    const refreshBtn = document.createElement('button');
    refreshBtn.type = 'button';
    refreshBtn.className = 'btn btn-primary';
    refreshBtn.innerHTML = `${KF_ICON.refresh}Aktualisieren`;
    refreshBtn.addEventListener('click', doRefresh);
    // Reihenfolge in der Toolbar: Exportieren, Aktualisieren, dann meta + Spalten-Konfig.
    tbRight.prepend(exportBtn, refreshBtn);
  }
  page.onAction('cols', () => page.toast('Spalten-Konfiguration folgt'));
}

async function loadLogPage(
  ctx: LogCtx,
  startRow: number,
  endRow: number,
  filters: { status: string; event: string; script: string; search: string },
): Promise<{ rows: LogRow[]; total: number | undefined }> {
  let query = ctx.supabase
    .schema('gambio')
    .from('activity_log')
    .select('id,event,status,storefront,ref_id,script_name,summary,detail,created_at', { count: 'exact' })
    .order('created_at', { ascending: false });
  if (filters.status) query = query.eq('status', filters.status);
  if (filters.event) query = query.eq('event', filters.event);
  if (filters.script) query = query.eq('script_name', filters.script);
  if (filters.search) {
    const like = `%${filters.search.replace(/[%,]/g, '')}%`;
    query = query.or(`ref_id.ilike.${like},summary.ilike.${like},script_name.ilike.${like}`);
  }
  const { data, count, error } = await query.range(startRow, endRow - 1);
  if (error) throw error;
  return { rows: (data ?? []) as LogRow[], total: count ?? undefined };
}

/** Distinct `script_name`-Werte für den Skript-Filter. PostgREST kann kein echtes
 *  DISTINCT → gebündelt über die letzten 1000 Log-Zeilen laden und client-seitig
 *  deduplizieren. Skripte, die ausschließlich in älteren (nicht in diesem Fenster
 *  liegenden) Zeilen vorkommen, fehlen ggf. im Dropdown - bewusste Grenze. */
async function loadScriptNames(ctx: LogCtx): Promise<string[]> {
  const { data } = await ctx.supabase
    .schema('gambio')
    .from('activity_log')
    .select('script_name')
    .not('script_name', 'is', null)
    .order('created_at', { ascending: false })
    .limit(1000);
  const set = new Set<string>();
  for (const r of (data ?? []) as Array<{ script_name: string | null }>) {
    if (r.script_name) set.add(r.script_name);
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

/* ---------- cell renderers ---------- */

/**
 * Öffnet den Bestellübersicht-Slot (`/#/orders`) und selektiert dort die Bestellung.
 * Voller Reload des iframes, ABER der Vario-`appToken` bleibt in der Query erhalten → keine
 * Re-Authentifizierung nötig. Gleiches Verfahren wie `openSettingsSection` in `tabs/orders.ts`.
 */
function openOrdersSlot(idOrder: string, shopKey: string): void {
  const url = new URL(window.location.href);
  url.searchParams.set('gambioOrderId', idOrder);
  url.searchParams.set('gambioOrderShop', shopKey);
  url.hash = '#/orders';
  window.location.assign(url.toString());
}

function htmlEl(html: string): HTMLElement {
  const e = document.createElement('span');
  e.style.display = 'inline-flex';
  e.style.alignItems = 'center';
  e.style.maxWidth = '100%';
  e.innerHTML = html;
  return e;
}

function statusPill(status: string): string {
  if (status === 'success') return `<span class="st st-ok"><span class="pd"></span>Erfolg</span>`;
  if (status === 'error') return `<span class="st st-err"><span class="pd"></span>Fehler</span>`;
  if (status === 'warning') return `<span class="st st-warn"><span class="pd"></span>Warnung</span>`;
  return `<span class="st">${escapeHtml(status)}</span>`;
}

function messageHtml(d: LogRow | undefined): string {
  if (!d?.summary) return '';
  const beleg = belegNumber(d);
  if (d.status === 'success' && beleg) {
    const url = varioDocumentUrl(belegDocumentId(d));
    const belegEl = url
      ? `<a href="${escapeHtml(url)}" target="_blank" rel="noopener" class="belg" title="Beleg in Vario öffnen" style="text-decoration:underline;text-underline-offset:2px;">${escapeHtml(beleg)}</a>`
      : `<span class="belg">${escapeHtml(beleg)}</span>`;
    return `<span class="c-msg">Beleg ${belegEl}</span>`;
  }
  return `<span class="c-msg">${escapeHtml(d.summary)}</span>`;
}

function actionsCell(): HTMLElement {
  const e = document.createElement('span');
  e.className = 'act-cell';
  e.innerHTML =
    `<button type="button" class="detail-btn" data-act="detail">${KF_ICON.detail}Detail</button>` +
    `<button type="button" class="act-icon" data-act="reprocess" title="Erneut verarbeiten">${KF_ICON.reprocess}</button>`;
  return e;
}

/* ---------- detail drawer ---------- */

function canFix(row: LogRow): boolean {
  const ms = (row.detail as Record<string, unknown> | null)?.['missingSkus'];
  return row.event === 'order_import' && row.status === 'error' && Array.isArray(ms) && ms.length > 0 && !!row.storefront && !!row.ref_id;
}

async function fixFromLog(row: LogRow, ctx: LogCtx, page: KfPage, refreshAll: () => void): Promise<void> {
  if (!row.storefront || !row.ref_id) return;
  const shopKey = row.storefront;
  const idOrder = row.ref_id;
  const fixBtn = document.querySelector<HTMLButtonElement>('[data-kf-drawer-foot] [data-act="fix"], [data-kf-drawer-foot] [data-kf-act="fix"]');
  const origHtml = fixBtn?.innerHTML ?? '';
  const setBusy = (busy: boolean): void => {
    if (!fixBtn) return;
    fixBtn.disabled = busy;
    fixBtn.innerHTML = busy
      ? `<span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Wird übernommen …`
      : origHtml;
  };
  // Abbruch, wenn der Nutzer den Drawer während des Pollings schließt.
  const ac = new AbortController();
  const closeBtn = document.querySelector<HTMLElement>('[data-kf-drawer-close]');
  const onClose = (): void => ac.abort();
  closeBtn?.addEventListener('click', onClose, { once: true });
  try {
    const baselineId = await latestActivityId(ctx.supabase, idOrder, shopKey);
    const { error } = await ctx.supabase.rpc('gambio_enqueue_fix_missing_articles', {
      _shop_key: shopKey, _id_order: idOrder,
    });
    if (error) throw error;
    setBusy(true);
    page.toast(`Behebung für ${idOrder} läuft …`);
    const outcome = await awaitOrderOutcome(ctx.supabase, { shopKey, idOrder, baselineId, signal: ac.signal });
    if (ac.signal.aborted) return;
    if (outcome.status === 'success') {
      page.toast(outcome.beleg ? `Auftrag ${idOrder} übernommen - Beleg ${outcome.beleg}` : `Auftrag ${idOrder} übernommen`);
      page.drawer.close();
    } else if (outcome.status === 'error') {
      page.toast(`Behebung fehlgeschlagen: ${outcome.summary}`);
      setBusy(false);
    } else {
      page.toast('Behebung läuft noch - Ansicht wird aktualisiert.');
      setBusy(false);
    }
  } catch (e) {
    page.toast(`Fehlgeschlagen: ${e instanceof Error ? e.message : String(e)}`);
    setBusy(false);
  } finally {
    closeBtn?.removeEventListener('click', onClose);
    refreshAll();
  }
}

function openDrawer(row: LogRow, page: KfPage, ctx: LogCtx, refreshAll: () => void): void {
  const { date, time } = splitDateTime(row.created_at);
  const belegUrl = varioDocumentUrl(belegDocumentId(row));
  page.drawer.open({
    eyebrow: 'Log-Eintrag',
    ref: row.ref_id ?? '—',
    fields: [
      { label: 'Zeitpunkt', html: `${escapeHtml(date)}, ${escapeHtml(time)} Uhr` },
      { label: 'Status', html: statusPill(row.status) },
      { label: 'Vorgang', html: escapeHtml(eventLabel(row.event)) },
      { label: 'Sprache', html: escapeHtml((row.storefront ?? '').toUpperCase()) },
      { label: 'Skript', html: `<span class="c-script" style="font-size:13.5px;color:var(--ink);">${escapeHtml(row.script_name ?? '—')}</span>`, full: true },
      { label: 'Meldung', html: escapeHtml(row.summary ?? '—'), full: true },
    ],
    payloadHtml: kfJsonPayload(row.detail ?? {}),
    footerHtml:
      `<button type="button" class="btn btn-ghost" data-kf-act="copy">${KF_ICON.copy}Payload kopieren</button>` +
      // Als echter <a target="_blank"> statt window.open - Letzteres wird im Vario-iframe
      // blockiert (Sandbox ohne allow-popups); Anchor-Navigation funktioniert dort.
      (belegUrl ? `<a href="${escapeHtml(belegUrl)}" target="_blank" rel="noopener" class="btn btn-primary">${KF_ICON.external}Beleg öffnen</a>` : '') +
      (canFix(row) ? `<button type="button" class="btn btn-primary" data-kf-act="fix">${KF_ICON.reprocess}Fehlende Artikel anlegen &amp; übernehmen</button>` : ''),
    onFooter: (id) => {
      if (id === 'copy') {
        void navigator.clipboard?.writeText(JSON.stringify(row.detail ?? {}, null, 2));
        page.toast('Payload kopiert');
      }
      if (id === 'fix') void fixFromLog(row, ctx, page, refreshAll);
    },
  });
}

/* ---------- reprocess (gambio_enqueue_order_create) ---------- */

async function retryImport(row: LogRow, ctx: LogCtx, page: KfPage, refreshAll: () => void): Promise<void> {
  // "Erneut verarbeiten" ist nur für Auftragsimporte verdrahtet (gambio_enqueue_order_create).
  if (row.event !== 'order_import') {
    page.toast('Erneut verarbeiten ist aktuell nur für Aufträge verfügbar.');
    return;
  }
  if (!row.storefront || !row.ref_id) {
    page.toast('Erneut verarbeiten nicht möglich - keine Referenz.');
    return;
  }
  const shopKey = row.storefront;
  const idOrder = row.ref_id;
  try {
    const baselineId = await latestActivityId(ctx.supabase, idOrder, shopKey);
    const { data, error } = await ctx.supabase.rpc('gambio_enqueue_order_create', {
      _shop_key: shopKey, _id_order: idOrder,
    });
    if (error) throw error;
    const reused = Array.isArray(data) ? data[0]?.reused : (data as { reused?: boolean } | null)?.reused;
    page.toast(reused ? `${idOrder} läuft bereits - warte auf Ergebnis …` : `${idOrder} wird erneut verarbeitet …`);
    const outcome = await awaitOrderOutcome(ctx.supabase, { shopKey, idOrder, baselineId });
    if (outcome.status === 'success') {
      page.toast(outcome.beleg ? `${idOrder} übernommen - Beleg ${outcome.beleg}` : `${idOrder} übernommen`);
    } else if (outcome.status === 'error') {
      page.toast(`Fehlgeschlagen: ${outcome.summary}`);
    } else {
      page.toast('Läuft noch - Ansicht wird aktualisiert.');
    }
  } catch (e) {
    page.toast(`Erneuter Start fehlgeschlagen: ${e instanceof Error ? e.message : String(e)}`);
  } finally {
    refreshAll();
  }
}

/* ---------- helpers ---------- */

function belegNumber(row: LogRow): string | null {
  const d = row.detail as Record<string, unknown> | null;
  const num = d?.['documentNumber'] ?? d?.['document_number'];
  if (typeof num === 'string' && num) return num;
  if (typeof num === 'number') return String(num);
  const m = row.summary?.match(/Beleg\s+(\S+)/i);
  return m ? m[1]! : null;
}

/** Vario-Dokument-ID fürs Deeplink - aus dem order_import-Detail (`result.varioOrderId`) bzw.
 *  `documentId`. Number-Strings, kein Parsen aus der Meldung (die enthält nur die Belegnummer). */
function belegDocumentId(row: LogRow): string | null {
  const d = row.detail as Record<string, unknown> | null;
  if (!d) return null;
  const result = d['result'] as Record<string, unknown> | undefined;
  const id = result?.['varioOrderId'] ?? result?.['documentId'] ?? d['documentId'] ?? d['document_id'];
  if (typeof id === 'string' && id) return id;
  if (typeof id === 'number') return String(id);
  return null;
}

/** Vario-Artikel-ID einer erfolgreichen article_import-Logzeile: variant_import → `result.mainArticleId`
 *  (Hauptartikel der Variantengruppe), flacher product_import → `result.articleId`. */
function logArticleId(row: LogRow | undefined): string | null {
  if (!row || row.event !== 'article_import' || row.status !== 'success') return null;
  const result = (row.detail as Record<string, unknown> | null)?.['result'] as Record<string, unknown> | undefined;
  const id = result?.['mainArticleId'] ?? result?.['articleId'];
  if (typeof id === 'string' && id) return id;
  if (typeof id === 'number') return String(id);
  return null;
}

function eventLabel(event: string): string {
  if (event === 'order_import') return 'Auftragsimport';
  if (event === 'article_import') return 'Artikelimport';
  if (event === 'listing_publish') return 'Aufschalten';
  return event;
}

function splitDateTime(iso: string | null): { date: string; time: string } {
  if (!iso) return { date: '', time: '' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: iso, time: '' };
  return {
    date: d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }),
    time: d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
  };
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
